import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from '../utils/toast';
import { api } from '../config/api';
import {
  Edit,
  Trash2,
  Printer,
  Plus,
  ArrowLeft,
  ShieldCheck,
  Save,
  RefreshCw,
  WalletCards,
  Search
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { formatDisplayDate } from '../utils/dateFormat';
import { isValidPhoneNumber, normalizePhoneInput } from '../utils/formValidation';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeader,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanelPadding,
  masterPrimaryButton,
  masterSearchInput,
  masterSearchWrap,
  masterSelect,
  masterSubtitle,
  masterTableData,
  masterTitle,
  masterToolbar,
  masterValueBox
} from '../components/masterPageStyles';

const initialFormState = {
  billId: '',
  patientName: '',
  patientPhone: '',
  appointmentRef: '',
  doctorName: '',
  consultationFee: '',
  discount: '',
  gstRate: '',
  paymentMode: 'Cash',
  paymentStatus: '',
};

const ConsultationBilling = () => {
  const location = useLocation();
  const [formData, setFormData] = useState(initialFormState);
  const [savedBill, setSavedBill] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [billingList, setBillingList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(true); 
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [submitAction, setSubmitAction] = useState('submit');

  const feeValue = useMemo(() => Number(formData.consultationFee || 0), [formData.consultationFee]);
  const discountValue = useMemo(() => Number(formData.discount || 0), [formData.discount]);
  const netAmount = Math.max(feeValue - discountValue, 0);
  const gstRateValue = Number(formData.gstRate || 0);
  const gstAmount = gstRateValue > 0 ? (netAmount * gstRateValue) / 100 : 0;
  const finalAmount = netAmount + gstAmount;

  const fetchNextBillId = async () => {
    try {
      const res = await api.get('/billing/next-id');
      if (res.data.success) {
        setFormData((prev) => ({ ...prev, billId: res.data.nextId }));
      }
    } catch {
      toast.error('FAILED TO GENERATE BILL ID');
    }
  };

  const fetchAppointments = async () => {
    try {
      const res = await api.get('/appointments/all');
      if (res.data.success) {
        setAppointments(res.data.data || []);
      }
    } catch {
      toast.error('FAILED TO LOAD APPOINTMENTS');
    }
  };

  const fetchDoctors = async () => {
    try {
      const res = await api.get('/doctors/all');
      if (res.data.success) {
        setDoctors(res.data.data || []);
      }
    } catch {
      toast.error('FAILED TO LOAD DOCTORS');
    }
  };

  const fetchBills = async () => {
    try {
      const res = await api.get('/billing/all');
      if (res.data.success) setBillingList(res.data.data || []);
    } catch {
      toast.error('FAILED TO LOAD BILLING LIST');
    }
  };

  useEffect(() => {
    
    fetchAppointments();
    fetchDoctors();
    fetchBills();
  }, []);

  useEffect(() => {
    const prefillPatient = location.state?.prefillFromPatient;
    if (!prefillPatient) return;

    setShowForm(true);
    setEditingId(null);
    setFormData((prev) => ({
      ...prev,
      patientName: prefillPatient.fullName || '',
      patientPhone: prefillPatient.mobileNumber || '',
      appointmentRef: prefillPatient.appointmentId || ''
    }));
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    if (!showForm || !formData.appointmentRef || !appointments.length) return;

    const selected = appointments.find((appt) => appt.appointmentId === formData.appointmentRef);
    if (!selected) return;

    const doctor = doctors.find((doc) => doc.doctorId === selected.doctorId);
    setFormData((prev) => ({
      ...prev,
      patientName: prev.patientName || selected.patientName || '',
      patientPhone: prev.patientPhone || selected.patientMobile || '',
      appointmentRef: selected.appointmentId || prev.appointmentRef,
      doctorName: selected.doctorName || doctor?.doctorName || prev.doctorName,
      consultationFee: doctor?.consultationFee ?? prev.consultationFee
    }));
  }, [showForm, formData.appointmentRef, appointments, doctors]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'patientPhone' ? normalizePhoneInput(value) : value,
    }));
  };

  const handleAppointmentSelect = (e) => {
    const value = e.target.value;
    const selected = appointments.find((appt) => appt.appointmentId === value);
    if (!selected) {
      setFormData((prev) => ({
        ...prev,
        patientName: '',
        patientPhone: '',
        appointmentRef: '',
        doctorName: '',
        consultationFee: ''
      }));
      return;
    }
    const doctor = doctors.find((doc) => doc.doctorId === selected.doctorId);
    setFormData((prev) => ({
      ...prev,
      patientName: selected.patientName || '',
      patientPhone: selected.patientMobile || '',
      appointmentRef: selected.appointmentId || '',
      doctorName: selected.doctorName || doctor?.doctorName || '',
      consultationFee: doctor?.consultationFee ?? prev.consultationFee
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.patientPhone && !isValidPhoneNumber(formData.patientPhone)) {
      return toast.error('Phone number must be exactly 10 digits');
    }
    try {
      const payload = {
        ...formData,
        consultationFee: feeValue,
        discount: discountValue,
        gstRate: gstRateValue
      };
      const response = editingId
        ? await api.put(`/billing/update/${editingId}`, payload)
        : await api.post('/billing/create', payload);
      setSavedBill(response.data.data);
      toast.success(editingId ? `Invoice ${response.data.data.billId} updated` : `Invoice ${response.data.data.billId} generated`);
      if (submitAction === 'print') {
        printBillPdf(response.data.data);
      }
      setEditingId(null);
      setShowForm(true);
      setFormData((prev) => ({
        ...prev,
        patientName: '',
        patientPhone: '',
        appointmentRef: '',
        doctorName: '',
        consultationFee: '',
        discount: '',
        gstRate: '',
        paymentMode: 'Cash'
      }));
     
      await fetchBills();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate invoice');
    }
  };

const buildBillPdf = (bill) => {
  // Standard A4 Portrait (210mm width x 297mm height)
  const doc = new jsPDF('p', 'mm', 'a4'); 
  const pageWidth = doc.internal.pageSize.getWidth();  // 210 mm
  const rightMargin = pageWidth - 10;
  const left = 10;
  let y = 8;

  // --- 0. BACKGROUND COLOR (Height set to 90) ---
  doc.setFillColor(207, 222, 231); // Light blue tint
  doc.rect(8, 28, pageWidth - 16, 90, 'F');

  // --- 1. ASHA THE HOPE HEADER ---
  doc.setFont('times', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(23, 37, 84); // Deep Navy Blue
  doc.text('ASHA THE HOPE', pageWidth / 2, y + 2, { align: 'center' });

  // NABL Accreditation (Top Right Block)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(185, 28, 28); // Red Accent
  doc.text('NABL', rightMargin, y, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(30, 41, 59);
  doc.text('Accredited', rightMargin, y + 2.8, { align: 'right' });
  doc.text('Laboratory', rightMargin, y + 5.5, { align: 'right' });

  // Subtitle
  y += 6;
  doc.setFont('times', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);
  doc.text('Diagnostic & Imaging LLP', pageWidth / 2, y, { align: 'center' });

  // Tagline
  y += 3.8;
  doc.setFont('times', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(180, 83, 9); // Gold accent
  doc.text("Diagnosis at it's best", pageWidth / 2, y, { align: 'center' });

  // Address & Contact Info
  y += 3.8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(30, 41, 59);
  doc.text('33, J. N. Tewari Road, Dumdum Cantonment (Near Dumdum Municipal Hospital), Kolkata - 700 028', pageWidth / 2, y, { align: 'center' });

  y += 3;
  doc.text('Phone : (033) 2529 4005 / 4006 | Mobile : 94323 07066 | E-mail : ashathehope2016@gmail.com', pageWidth / 2, y, { align: 'center' });

  y += 2.5;
  doc.setDrawColor(148, 163, 184); 
  doc.setLineWidth(0.35);
  doc.line(left, y, rightMargin, y);

  // --- 2. BILL TO & METADATA GRID ---
  y += 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(71, 85, 105);
  doc.text('BILL TO', left, y);

  doc.text('Invoice No.:', 140, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(15, 23, 42);
  doc.text(`${bill.billId || '-'}`, rightMargin, y, { align: 'right' });

  y += 3.8;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(`${bill.patientName || 'Walk-in Patient'}`, left, y);
  
  doc.setTextColor(71, 85, 105);
  doc.text('Issue date:', 140, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(15, 23, 42);
  doc.text(`${formatDisplayDate(bill.createdAt || Date.now())}`, rightMargin, y, { align: 'right' });

  y += 3.8;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text(`Phone: ${bill.patientPhone || '-'}`, left, y);
  
  y += 3.8;
  doc.text(`Appt Ref: ${bill.appointmentRef || '-'}`, left, y);

  // --- 3. ITEMS TABLE HEADER ---
  y += 3.5;
  doc.setFillColor(248, 250, 252); 
  doc.rect(left, y, pageWidth - 20, 4.5, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(71, 85, 105);
  doc.text('DESCRIPTION', left + 2, y + 3.2);
  doc.text('QUANTITY', 125, y + 3.2, { align: 'right' });
  doc.text('UNIT PRICE (Rs)', 160, y + 3.2, { align: 'right' });
  doc.text('AMOUNT (Rs)', rightMargin - 2, y + 3.2, { align: 'right' });

  // --- 4. TABLE LINE ITEMS ---
  y += 4.5;
  doc.setDrawColor(226, 232, 240);
  doc.line(left, y, rightMargin, y);
  
  y += 3.8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(15, 23, 42);
  
  doc.text(`Regular medical check up & consulting (${bill.doctorName || 'General Staff'})`, left + 2, y);
  doc.text('1 pcs', 125, y, { align: 'right' });
  doc.text(`${Number(bill.consultationFee || 0).toFixed(2)}`, 160, y, { align: 'right' });
  doc.text(`${Number(bill.consultationFee || 0).toFixed(2)}`, rightMargin - 2, y, { align: 'right' });

  y += 2;
  doc.setDrawColor(241, 245, 249);
  doc.line(left, y, rightMargin, y);

  // --- 5. BREAKDOWN TOTALS BLOCK ---
  y += 3.5;
  const totalsLeft = 135;
  const totalsWidth = rightMargin - totalsLeft;
  
  // Subtotal Row
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(71, 85, 105);
  doc.text('Subtotal:', totalsLeft + 2, y);
  doc.setTextColor(15, 23, 42);
  doc.text(`${Number(bill.consultationFee || 0).toFixed(2)}`, rightMargin - 2, y, { align: 'right' });

  // Discount Row
  if (Number(bill.discount || 0) > 0) {
    y += 3.8;
    doc.setTextColor(71, 85, 105);
    doc.text('Discount:', totalsLeft + 2, y);
    doc.setTextColor(225, 29, 72); 
    doc.text(`-${Number(bill.discount).toFixed(2)}`, rightMargin - 2, y, { align: 'right' });
  }

  // Taxes Row
  if (Number(bill.gstRate || 0) > 0) {
    y += 3.8;
    doc.setTextColor(71, 85, 105);
    doc.text(`GST (${bill.gstRate}%):`, totalsLeft + 2, y);
    doc.setTextColor(15, 23, 42);
    doc.text(`${Number(bill.gstAmount || 0).toFixed(2)}`, rightMargin - 2, y, { align: 'right' });
  }

  // --- 6. FINAL DUE BANNER BLOCK ---
  y += 2.5;
  doc.setFillColor(15, 23, 42); 
  doc.rect(totalsLeft, y, totalsWidth, 5, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255); 
  doc.text('TOTAL DUE (Rs)', totalsLeft + 2, y + 3.5);
  doc.text(`${Number(bill.finalAmount || 0).toFixed(2)}`, rightMargin - 2, y + 3.5, { align: 'right' });

  // --- 7. CUTTING LINE ---
  // Dashed Cut Line right below the 90mm box boundary (at 122mm mark)
  doc.setDrawColor(203, 213, 225);
  doc.setLineDashPattern([2, 2], 0);
  doc.line(left, 120, rightMargin, 120);

  return doc;
};

  const printBillPdf = (bill) => {
    const doc = buildBillPdf(bill);
    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    const printWindow = window.open(blobUrl, '_blank');
    if (printWindow) {
      printWindow.focus();
    }
  };

  const handleEdit = (bill) => {
    setEditingId(bill._id);
    setFormData({
      billId: bill.billId || '',
      patientName: bill.patientName || '',
      patientPhone: bill.patientPhone || '',
      appointmentRef: bill.appointmentRef || '',
      doctorName: bill.doctorName || '',
      consultationFee: bill.consultationFee ?? 0,
      discount: bill.discount ?? 0,
      gstRate: bill.gstRate ?? '',
      paymentMode: bill.paymentMode || 'Cash',
      paymentStatus: bill.paymentStatus || ''
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (bill) => {
    const confirmed = window.confirm(`Delete invoice ${bill.billId}?`);
    if (!confirmed) return;
    try {
      const res = await api.delete(`/billing/${bill._id}`);
      if (res.data.success) {
        toast.success('BILL DELETED');
        await fetchBills();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'DELETE FAILED');
    }
  };

  const resetForm = async () => {
    setEditingId(null);
    setFormData((prev) => ({
      ...prev,
      patientName: '',
      patientPhone: '',
      appointmentRef: '',
      doctorName: '',
      consultationFee: '',
      discount: '',
      gstRate: '',
      paymentMode: 'Cash',
      paymentStatus: ''
    }));
    await fetchNextBillId();
  };

  const openForm = async () => {
    await resetForm();
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(true);
    setEditingId(null);
    resetForm();
  };
  const handleFetchAppointmentData = () => {
    const query = formData.appointmentRef.trim().toLowerCase();
    if (!query) {
      return toast.error('Please enter Name, Mobile, or Appointment ID to fetch');
    }

    const foundAppt = appointments.find((appt) => {
      const apptId = (appt.appointmentId || '').toLowerCase();
      const mob = (appt.patientMobile || '').toLowerCase();
      const name = (appt.patientName || '').toLowerCase();

      return apptId === query || mob === query || name.includes(query);
    });

    if (!foundAppt) {
      return toast.error('No matching appointment found');
    }

    const doctor = doctors.find((doc) => doc.doctorId === foundAppt.doctorId);
    setFormData((prev) => ({
      ...prev,
      patientName: foundAppt.patientName || '',
      patientPhone: foundAppt.patientMobile || '',
      appointmentRef: foundAppt.appointmentId || '',
      doctorName: foundAppt.doctorName || doctor?.doctorName || '',
      consultationFee: doctor?.consultationFee ?? prev.consultationFee
    }));

    toast.success('Appointment data fetched successfully!');
  };

  const appointmentOptions = useMemo(
    () =>
      appointments.map((appt) => ({
        id: appt.appointmentId || '',
        label: `${appt.patientName || 'UNKNOWN'} | ${appt.patientMobile || 'N/A'} | ${appt.appointmentId || '-'}`
      })),
    [appointments]
  );

  const filteredBillingList = useMemo(() => {
    const sortedList = [...billingList].sort((a, b) => {
      const idA = String(a.billId || a._id || '');
      const idB = String(b.billId || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((bill) => {
      const billId = (bill.billId || '').toLowerCase();
      const patientName = (bill.patientName || '').toLowerCase();
      const patientPhone = (bill.patientPhone || '').toLowerCase();
      const doctorName = (bill.doctorName || '').toLowerCase();

      return (
        billId.startsWith(q) ||
        patientName.startsWith(q) ||
        patientPhone.startsWith(q) ||
        doctorName.startsWith(q)
      );
    });
  }, [billingList, searchTerm]);

  const paginatedBillingList = useMemo(() => {
    if (pageSize === -1) return filteredBillingList;
    const start = (currentPage - 1) * pageSize;
    return filteredBillingList.slice(start, start + pageSize);
  }, [filteredBillingList, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filteredBillingList.length, pageSize]);

  return (
    <div className={masterPageShell}>
      <header className={masterHeader}>
        <div className={`${masterHeaderCard} max-w-7xl`}>
          <div className="flex items-center gap-1">
            <div className={masterHeaderIcon}>
              <WalletCards size={18} />
            </div>
            <div>
              <h1 className={masterTitle}>Consultation Billing</h1>
              <p className={masterSubtitle}>Billing & Revenue Management</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
           <button 
              type="button" 
              onClick={() => setShowForm(!showForm)} 
              className={showForm ? masterPrimaryButton : masterAccentButton}
            >
              {showForm ? <ArrowLeft size={14} /> : <Plus size={14} />}
              {showForm ? 'VIEW BILLING LIST' : 'OPEN BILLING FORM'}
            </button>
            {/* {savedBill?.billId && (
              <div className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-1 py-1 text-[10px] font-bold uppercase text-white">
                <ShieldCheck size={14} /> {savedBill.billId}
              </div>
            )} */}
          </div>
        </div>
      </header>

      <main className="px-1 pb-1">
        <div className="mx-auto max-w-7xl">
          {!showForm ? (
            <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
                <div className="border-b border-slate-200 flex-shrink-0">
                  <SearchBar 
                    value={searchTerm} 
                    onChange={(val) => {
                      setSearchTerm(val);
                      setCurrentPage(1);
                    }} 
                    placeholder="Search alphabetically by bill ID, patient, phone, or doctor..." 
                    totalItems={filteredBillingList.length} 
                    currentPage={currentPage} 
                    pageSize={pageSize} 
                    onPageChange={setCurrentPage} 
                    onPageSizeChange={setPageSize} 
                  />
                </div>

                <div className="max-h-[calc(100vh-250px)] overflow-auto">
            <table className="w-full min-w-[1000px] table-fixed text-left text-[10px]">
              <thead className="sticky top-0 z-10 bg-slate-100 text-[9px] font-extrabold uppercase tracking-[0.14em] text-emerald-600 border-b border-slate-400">
                    <tr>
                      <th className={masterTableData}>S.No</th>
                      <th className={masterTableData}>Bill ID</th>
                      <th className={masterTableData}>Patient</th>
                      <th className={masterTableData}>Mobile</th>
                      <th className={masterTableData}>Doctor</th>
                      <th className={masterTableData}>Fee</th>
                      <th className={masterTableData}>Disc</th>
                      <th className={masterTableData}>GST</th>
                      <th className={masterTableData}>Final</th>
                      <th className={masterTableData}>Payment</th>
                      <th className={masterTableData}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-300 font-bold text-slate-700">
                    {paginatedBillingList.length > 0 ? paginatedBillingList.map((bill, index) => (
                      <tr key={bill._id} className="hover:bg-emerald-50/30">
                        <td className={masterTableData}>{pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}</td>
                        <td className={masterTableData}>{bill.billId}</td>
                        <td className={masterTableData}>{bill.patientName || '-'}</td>
                        <td className={masterTableData}>{bill.patientPhone || '-'}</td>
                        <td className={masterTableData}>{bill.doctorName || '-'}</td>
                        <td className={masterTableData}>{Number(bill.consultationFee || 0).toFixed(0)}</td>
                        <td className={masterTableData}>{Number(bill.discount || 0).toFixed(0)}</td>
                        <td className={masterTableData}>{Number(bill.gstRate || 0).toFixed(0)}%</td>
                        <td className={masterTableData}>{Number(bill.finalAmount || 0).toFixed(2)}</td>
                        <td className={masterTableData}>
                          <span className="rounded px-1 py-0.5 text-[9px] bg-slate-100 uppercase">{bill.paymentMode}</span>
                        </td>
                        <td className={masterTableData}>
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => handleEdit(bill)} className="p-1. text-emerald-600 bg-slate-50 rounded-lg hover:bg-emerald-600 hover:text-white transition-colors">
                              <Edit size={14} />
                              </button>
                            <button onClick={() => handleDelete(bill)} className="p-1 text-rose-600 bg-rose-50 rounded-lg hover:bg-rose-600 hover:text-white transition-colors">
                              <Trash2 size={14} />
                              </button>
                            <button onClick={() => printBillPdf(bill)} className="p-1 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-800 hover:text-white transition-colors">
                              <Printer size={14} />
                              </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="11" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">No billing records found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-1">
             <div className={masterPanelPadding}>
                <div className="flex flex-col gap-2">
                  <label className={masterInputLabel}>Search Appointment / Patient</label>
                  
                  {/* Input and Fetch Button Wrapper - fully responsive for mobile, tablet, and desktop */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                      list="appointment-options"
                      name="appointmentRef"
                      value={formData.appointmentRef}
                      onChange={handleInputChange}
                      placeholder="Type name, phone, or appointment ID..."
                      className={`${masterInput} flex-1`}
                    />
                    <button
                      type="button"
                      onClick={handleFetchAppointmentData}
                      className={`${masterPrimaryButton} justify-center py-2 sm:py-1 px-4 whitespace-nowrap`}
                    >
                      <Search size={14} /> Fetch Data
                    </button>
                  </div>

                  <datalist id="appointment-options">
                    {appointmentOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </datalist>
                </div>
              </div>

              <div className={masterPanelPadding}>
                <div className="grid grid-cols-1 gap-x-1 gap-y-1 md:grid-cols-4">
                  {/* <Input label="Bill ID" name="billId" value={formData.billId} readOnly /> */}
                  <Input label="Patient Name" name="patientName" value={formData.patientName} onChange={handleInputChange} />
                  <Input label="Phone Number" name="patientPhone" value={formData.patientPhone} onChange={handleInputChange} inputMode="numeric" maxLength={10} />
                  <Input label="Appt Reference" name="appointmentRef" value={formData.appointmentRef} onChange={handleInputChange} />
                  <Input label="Doctor" name="doctorName" value={formData.doctorName} onChange={handleInputChange} />
                  <Input label="Consultation Fee" name="consultationFee" value={formData.consultationFee} readOnly />
                  <Input label="Discount (INR)" name="discount" type="number" value={formData.discount} onChange={handleInputChange} />
                  <Input label="GST %" name="gstRate" type="number" value={formData.gstRate} onChange={handleInputChange} required={false} />
                  <Select label="Payment Mode" name="paymentMode" value={formData.paymentMode} onChange={handleInputChange} options={['Cash', 'UPI', 'Card'].map((item) => ({ value: item, label: item }))} />
                    <Select 
                      label="Payment Status" 
                      name="paymentStatus" 
                      value={formData.paymentStatus} 
                      onChange={handleInputChange} 
                      required={true}
                      options={[
                        { value: '', label: '-- SELECT STATUS --' },
                        { value: 'Paid', label: 'PAID' },
                        { value: 'Unpaid', label: 'UNPAID' }
                      ]} 
                    />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-1 md:grid-cols-3">
                <Amount label="Net Amount" value={netAmount} />
                <Amount label="GST Amount" value={gstAmount} />
                <Amount label="Final Payable" value={finalAmount} />
              </div>

              <div className="grid grid-cols-1 gap-1 md:grid-cols-3">
                {/* <button type="submit" className={`${masterAccentButton} justify-center py-2.5`}>
                  <Save size={16} /> {editingId ? 'Update Billing' : 'Submit Billing'}
                </button> */}
                <button type="submit" onClick={() => setSubmitAction('submit')} className={`${masterPrimaryButton} justify-center py-1`}>
                  <Save size={16} /> {editingId ? 'Update' : 'Submit'}
                </button>
                <button type="submit" onClick={() => setSubmitAction('print')} className={`${masterAccentButton} justify-center py-1`}>
                  <Printer size={16} /> {editingId ? 'Update & Print' : 'Submit & Print'}
                </button>
                <button type="button" onClick={resetForm} className={`${masterPrimaryButton} justify-center py-1`}>
                  <RefreshCw size={14} /> Reset Form
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

const Input = ({ label, ...props }) => (
  <div className="flex flex-col ">
    <label className={masterInputLabel}>{label}</label>
    <input {...props} className={masterInput} />
  </div>
);

const Select = ({ label, options, ...props }) => (
  <div className="flex flex-col ">
    <label className={masterInputLabel}>{label}</label>
    <select {...props} className={masterSelect}>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

const Amount = ({ label, value }) => (
  <div className={masterValueBox}>
    <div className={masterInputLabel}>{label}</div>
    <div className="text-lg font-black text-slate-900">Rs {value.toFixed(2)}</div>
  </div>
);

export default ConsultationBilling;
